import { useState, useEffect, useRef } from 'react';
import {
  fetchProducts,
  fetchCategories,
  fetchFormulas,
  fetchProductTypes,
  fetchConcerns,
  isBestsellerProduct,
  getProductFormula,
  getSaleBadgeText,
  isProductOnSale,
  fetchBannerSettings,
  getBannerText,
  decodeText
} from '../api/woocommerce';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

const ProductGrid = () => {
  const [products, setProducts] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resultsCount, setResultsCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [bannerSettings, setBannerSettings] = useState(null);
  const [isFilterAnimating, setIsFilterAnimating] = useState(false);
  const [showDesktopFilters, setShowDesktopFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [gridVisible, setGridVisible] = useState(true);
  const [gridColumns, setGridColumns] = useState('grid-cols-2 md:grid-cols-4');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedConcern, setSelectedConcern] = useState(null);
  const [selectedFormula, setSelectedFormula] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  const desktopFiltersRef = useRef(null);

  const getAcfField = (product, fieldName, fallback = '') => {
    return product.acf && product.acf[fieldName] ? product.acf[fieldName] : fallback;
  };
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setMobileSearchTerm(searchParam);
    }
  }, []);

  const debounceRef = useRef();

  const toggleFilters = () => {
    const isMobile = window.innerWidth < 768;

    if (showFilters) {
      // Closing filters
      if (isMobile) {
        setShowMobileFilters(false);
        setTimeout(() => {
          setShowFilters(false);
        }, 300);
      } else {
        setIsFilterAnimating(true);
        setGridVisible(false);

        setShowDesktopFilters(false);
        setTimeout(() => {
          setShowDesktopFilters(false);
          setTimeout(() => {

            setGridColumns('grid-cols-2 md:grid-cols-4');
            setShowFilters(false);
            setIsFilterAnimating(false);
            setTimeout(() => {
              setGridVisible(true);
            }, 50);
          }, 200);
        }, 100);
      }
    } else {
      // Opening filters
      if (isMobile) {
        setShowFilters(true);
        setTimeout(() => {
          setShowMobileFilters(true);
        }, 50);
      } else {
        setIsFilterAnimating(true);
        setGridVisible(false);

        setTimeout(() => {
          setShowFilters(true);
          setGridColumns('grid-cols-2 md:grid-cols-3');

          setTimeout(() => { // Then show filters
            setShowDesktopFilters(true);
            setTimeout(() => { // Finally show grid with new layout
              setGridVisible(true);
            }, 100);
          }, 50);
        }, 100);
      }
    }
  };

  useEffect(() => {
    const handleLocationChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const searchParam = urlParams.get('search');
      setMobileSearchTerm(searchParam || '');
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const { currentLanguage } = useLanguage();
  const [orderBy, setOrderBy] = useState('menu_order');

  const sortProductsByFormulaAndNumber = (products) => {
    const priorityProducts = products.filter(product => product.acf?.priority === true);
    const regularProducts = products.filter(product => product.acf?.priority !== true);

    const sortGroup = (group) => {
      // Separate products with and without formulas
      const withFormula = [];
      const withoutFormula = [];

      group.forEach(product => {
        const formula = getProductFormula(product);
        // Check if product is a bundle by name or if it explicitly has no formula
        const isBundle = product.name.toLowerCase().includes('bundle') ||
          product.name.toLowerCase().includes('duo') ||
          product.name.toLowerCase().includes('trio');

        console.log('Product:', product.name, 'Formula:', formula, 'Formula count:', product.formula?.length, 'Is bundle:', isBundle);

        // If it's a bundle, treat it as having no formula regardless of what getProductFormula returns
        if (formula && !isBundle) {
          withFormula.push(product);
        } else {
          withoutFormula.push(product);
        }
      });

      // Sort products WITH formula by formula slug, then by product number
      const sortedWithFormula = [];
      let i = 0;

      while (i < withFormula.length) {
        const currentFormulaSlug = getProductFormula(withFormula[i])?.slug || '';
        const sameFormulaGroup = [];

        while (i < withFormula.length && (getProductFormula(withFormula[i])?.slug || '') === currentFormulaSlug) {
          sameFormulaGroup.push(withFormula[i]);
          i++;
        }

        sameFormulaGroup.sort((a, b) => {
          const aProductNumber = a.acf?.product_number || '';
          const bProductNumber = b.acf?.product_number || '';
          return aProductNumber.localeCompare(bProductNumber);
        });

        sortedWithFormula.push(...sameFormulaGroup);
      }

      // Sort products WITHOUT formula by date created descending (newest first)
      withoutFormula.sort((a, b) => {
        const aDate = new Date(a.date_created || 0);
        const bDate = new Date(b.date_created || 0);
        return bDate - aDate; // Descending order (newest first)
      });

      // Interleave products without formula based on their creation date
      const result = [];
      let withFormulaIndex = 0;
      let withoutFormulaIndex = 0;

      // Debug logging
      console.log('Products with formula:', sortedWithFormula.map(p => ({ name: p.name, date: p.date_created })));
      console.log('Products without formula:', withoutFormula.map(p => ({ name: p.name, date: p.date_created })));

      while (withFormulaIndex < sortedWithFormula.length || withoutFormulaIndex < withoutFormula.length) {
        // Check if we should insert a product without formula
        if (withoutFormulaIndex < withoutFormula.length) {
          const noFormulaProduct = withoutFormula[withoutFormulaIndex];
          const noFormulaDate = new Date(noFormulaProduct.date_created || 0);

          // Check if this no-formula product should come before the next with-formula product
          if (withFormulaIndex >= sortedWithFormula.length) {
            // No more products with formula, add remaining without formula
            result.push(noFormulaProduct);
            withoutFormulaIndex++;
            continue;
          }

          const withFormulaProduct = sortedWithFormula[withFormulaIndex];
          const withFormulaDate = new Date(withFormulaProduct.date_created || 0);

          console.log('Comparing:', noFormulaProduct.name, noFormulaDate, 'vs', withFormulaProduct.name, withFormulaDate);

          // For descending order: if no-formula product is NEWER (greater date), it should come first
          if (noFormulaDate >= withFormulaDate) {
            result.push(noFormulaProduct);
            withoutFormulaIndex++;
          } else {
            result.push(withFormulaProduct);
            withFormulaIndex++;
          }
        } else {
          // No more products without formula, add remaining with formula
          result.push(sortedWithFormula[withFormulaIndex]);
          withFormulaIndex++;
        }
      }

      console.log('Final result:', result.map(p => p.name));
      return result;
    };

    const sortedPriorityProducts = sortGroup(priorityProducts);
    const sortedRegularProducts = sortGroup(regularProducts);

    return [...sortedPriorityProducts, ...sortedRegularProducts];
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const loadData = async () => {
        setLoading(true);
        const isMobile = window.innerWidth < 768;
        if (!isMobile && showFilters) {
          setIsFilterLoading(true);
          setGridVisible(false);
        }

        try {
          const [
            productsData,
            categoriesData,
            formulasData,
            productTypesData,
            concernsData,
            bannerData
          ] = await Promise.all([
            fetchProducts({
              perPage: 100,
              orderBy,
              order: sortOrder,
              productT: selectedType,
              concern: selectedConcern,
              formula: selectedFormula,
              searchTerm: mobileSearchTerm || undefined,
              language: currentLanguage
            }),
            fetchCategories(),
            fetchFormulas(currentLanguage),
            fetchProductTypes(currentLanguage),
            fetchConcerns(currentLanguage),
            fetchBannerSettings(currentLanguage)
          ]);
          let finalProducts = productsData.products;
          if (orderBy === 'menu_order') {
            finalProducts = sortProductsByFormulaAndNumber(productsData.products);
          }
          setProducts(finalProducts);
          setResultsCount(productsData.products.length);
          setFormulas(formulasData);
          setProductTypes(productTypesData);
          setConcerns(concernsData);
          setBannerSettings(bannerData);

        } catch (err) {
          console.error('Error loading data:', err);
          setIsFilterLoading(false);
          setGridVisible(true);
        } finally {
          setLoading(false);
          setIsFilterLoading(false);
          setGridVisible(true);
          const isMobile = window.innerWidth < 768;
          if (!isMobile) {
            if (isFilterLoading) {
              setTimeout(() => {
                setIsFilterLoading(false);
                setGridVisible(true);
              }, 450);
            } else {
              setGridVisible(true);
            }
          }
        }
      };

      loadData();
    }, 10);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [selectedType, selectedConcern, selectedFormula, orderBy, sortOrder, mobileSearchTerm, currentLanguage]);

  const handleMobileSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const searchTerm = formData.get('search').trim();
    setMobileSearchTerm(searchTerm);
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('search', searchTerm);
    window.history.pushState({}, '', newUrl);
  };

  return (
    <div className='px-2'>
      {/* Mobile Search */}
      {mobileSearchTerm && (
        <div >
          <span className="text-14 font-light">({resultsCount} {t('productGrid.results')})</span>
          <form onSubmit={handleMobileSearch} className="mt-2">
            <div className="relative">
              <input
                type="text"
                name="search"
                placeholder={t('search.placeholder')}
                defaultValue={mobileSearchTerm}
                className="w-full pl-[9px] pt-[9px] pb-[8px] pr-10 border border-black focus:outline-none text-14 font-normal"
              />
              <button
                type="submit"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 focus:outline-none"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_2005_226)">
                    <path d="M6.90909 12.7273C10.1224 12.7273 12.7273 10.1224 12.7273 6.90909C12.7273 3.6958 10.1224 1.09091 6.90909 1.09091C3.6958 1.09091 1.09091 3.6958 1.09091 6.90909C1.09091 10.1224 3.6958 12.7273 6.90909 12.7273Z" stroke="black" strokeMiterlimit="10" />
                    <path d="M11.2727 11.2727L14.9091 14.9091" stroke="black" strokeMiterlimit="10" />
                  </g>
                  <defs>
                    <clipPath id="clip0_2005_226">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
          </form>
        </div>
      )}

      <div className={`mb-[64px]`} style={mobileSearchTerm ? { paddingTop: '19px' } : {}}>
        <div className="flex-1">
          <h1 className={`font-light text-26 leading-auto mb-2 ${mobileSearchTerm ? 'hidden' : ''}`}>{t('footer.shopAll')}</h1>

          <div className={`flex justify-between items-center mb-6 ${mobileSearchTerm ? 'hidden' : ''}`}>
            <button
              className="md:flex md:items-center md:text-14 md:font-light hidden"
              onClick={toggleFilters}
            >
              <svg className="-translate-y-[1px]" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_6001_1484)">
                  <path d="M0.727295 5.09091H9.45457" stroke="black" strokeMiterlimit="10" />
                  <path d="M11.6364 5.09091H15.2727" stroke="black" strokeMiterlimit="10" />
                  <path d="M6.54541 10.9091H15.2727" stroke="black" strokeMiterlimit="10" />
                  <path d="M0.727295 10.9091H4.36366" stroke="black" strokeMiterlimit="10" />
                  <path d="M12 3.27273H9.09094V6.90909H12V3.27273Z" stroke="black" strokeMiterlimit="10" />
                  <path d="M6.90909 9.09091H4V12.7273H6.90909V9.09091Z" stroke="black" strokeMiterlimit="10" />
                </g>
                <defs>
                  <clipPath id="clip0_6001_1484">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <span className="text-14 font-light ml-2">{showFilters ? t('productGrid.hideFilters') : t('productGrid.showFilters')}</span>
            </button>
            <button
              className="flex items-center text-14 font-light md:hidden"
              onClick={toggleFilters}
            >
              <svg className="-translate-y-[1px]" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_6001_1484)">
                  <path d="M0.727295 5.09091H9.45457" stroke="black" strokeMiterlimit="10" />
                  <path d="M11.6364 5.09091H15.2727" stroke="black" strokeMiterlimit="10" />
                  <path d="M6.54541 10.9091H15.2727" stroke="black" strokeMiterlimit="10" />
                  <path d="M0.727295 10.9091H4.36366" stroke="black" strokeMiterlimit="10" />
                  <path d="M12 3.27273H9.09094V6.90909H12V3.27273Z" stroke="black" strokeMiterlimit="10" />
                  <path d="M6.90909 9.09091H4V12.7273H6.90909V9.09091Z" stroke="black" strokeMiterlimit="10" />
                </g>
                <defs>
                  <clipPath id="clip0_6001_1484">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <span className="text-14 font-light ml-2">{t('productGrid.showFilters')}</span>
            </button>

            <span className="text-14 font-light">({resultsCount} {t('productGrid.results')})</span>
          </div>

          <div className="flex flex-col md:flex-row gap-2">
            {/* Desktop Filters */}
            {showFilters && (
              <div
                className={`hidden md:block bg-white mb-4 md:mb-0 transition-all duration-300 ease-in-out overflow-hidden ${showDesktopFilters
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 -translate-x-full'
                  }`}
                style={{
                  width: 'calc(25% - 0.375rem)'
                }}
              >
                <div className="text-14 font-light">
                  <div className="mb-[32px]">
                    <ul>
                      <li className={`cursor-pointer pb-2 ${!selectedFormula ? 'font-medium text-14' : ''}`}
                        onClick={() => setSelectedFormula(null)}>{t('footer.shopAll')}</li>
                      {formulas.map((formula, index) => (
                        <li
                          key={`formula-${formula.id}`}
                          className={`cursor-pointer ${selectedFormula === formula.slug ? 'font-medium text-14' : ''
                            } ${index !== formulas.length - 1 ? 'pb-2' : ''}`}
                          onClick={() =>
                            setSelectedFormula(selectedFormula === formula.slug ? null : formula.slug)
                          }
                        >
                          [{formula.slug} formulas] {formula.name}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-[32px]">
                    <h2 className="font-light text-10 mb-2">{t('productGrid.productType')}</h2>
                    <ul>
                      <li
                        className={`pb-2 cursor-pointer ${!selectedType ? 'font-medium text-14' : ''}`}
                        onClick={() => setSelectedType(null)}
                      >
                        {t('productGrid.allProducts')}
                      </li>
                      {productTypes.map((type, index) => (
                        <li
                          key={`type-${type.id}`}
                          className={`${index !== productTypes.length - 1 ? 'pb-2' : ''} cursor-pointer ${selectedType === type.slug ? 'font-medium text-14' : ''}`}
                          onClick={() => setSelectedType(selectedType === type.slug ? null : type.slug)}
                        >
                          {type.name}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-[32px]">
                    <h2 className="font-light text-10 mb-2">{t('productGrid.concern')}</h2>
                    <ul>
                      <li
                        className={`pb-2 cursor-pointer ${!selectedConcern ? 'font-medium text-14' : ''}`}
                        onClick={() => setSelectedConcern(null)}
                      >
                        {t('productGrid.allProducts')}
                      </li>

                      {concerns.map((concern, index) => (
                        <li
                          key={`concern-${concern.id}`}
                          className={`${index !== concerns.length - 1 ? 'pb-2' : ''} cursor-pointer ${selectedConcern === concern.slug ? 'font-medium text-14' : ''}`}
                          onClick={() => setSelectedConcern(selectedConcern === concern.slug ? null : concern.slug)}
                        >
                          {concern.name}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-2">
                    <h2 className="font-light text-10 mb-2">{t('productGrid.sortBy')}</h2>
                    <ul>
                      <li
                        className={`pb-2 cursor-pointer ${orderBy === 'date' && sortOrder === 'desc' ? 'font-medium text-14' : ''}`}
                        onClick={() => { setOrderBy('date'); setSortOrder('desc'); }}
                      >
                        {t('productGrid.latest')}
                      </li>
                      <li
                        className={` cursor-pointer ${orderBy === 'popularity' && sortOrder === 'desc' ? 'font-medium text-14' : ''}`}
                        onClick={() => { setOrderBy('popularity'); setSortOrder('desc'); }}
                      >
                        {t('productGrid.bestsellers')}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Filters */}
            {showFilters && (
              <div className="fixed inset-0 z-50 overflow-hidden md:hidden">
                {/* Overlay */}
                <div
                  className={`absolute inset-0 bg-black transition-opacity duration-300 ${showMobileFilters ? 'opacity-80' : 'opacity-0'}`}
                  onClick={toggleFilters}
                />

                {/* Filter Panel */}
                <div
                  className={`absolute inset-y-0 left-0 w-[358px] bg-white transition-transform duration-300 flex flex-col ${showMobileFilters ? 'translate-x-0' : '-translate-x-full'}`}
                >
                  {/* Header */}
                  <div className="flex-shrink-0 relative">
                    <button
                      type="button"
                      onClick={toggleFilters}
                      className="absolute top-[10px] right-2 cursor-pointer z-50 p-2"
                      aria-label="Close filters"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_4006_60)">
                          <path d="M14.1745 14.1745L1.82544 1.82544" stroke="black" strokeMiterlimit="10" />
                          <path d="M1.82544 14.1745L14.1745 1.82544" stroke="black" strokeMiterlimit="10" />
                        </g>
                        <defs>
                          <clipPath id="clip0_4006_60">
                            <rect width="16" height="16" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                  </div>

                  {/* Filter Content */}
                  <div className="flex-1 overflow-y-auto px-2 pt-[93px] pb-[80px] min-h-0">
                    <div className="text-14 font-light">
                      <div className="mb-[32px]">
                        <ul>
                          <li className={`cursor-pointer pb-2 ${!selectedFormula ? 'font-medium text-14' : ''}`}
                            onClick={() => setSelectedFormula(null)}>{t('footer.shopAll')}</li>
                          {formulas.map((formula, index) => (
                            <li
                              key={`formula-${formula.id}`}
                              className={`cursor-pointer ${selectedFormula === formula.slug ? 'font-medium text-14' : ''
                                } ${index !== formulas.length - 1 ? 'pb-2' : ''}`}
                              onClick={() => setSelectedFormula(selectedFormula === formula.slug ? null : formula.slug)}
                            >
                              [{formula.slug} formulas] {formula.name}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mb-[32px]">
                        <h2 className="font-light text-10 mb-2">{t('productGrid.productType')}</h2>
                        <ul>
                          <li
                            className={`pb-2 cursor-pointer ${!selectedType ? 'font-medium text-14' : ''}`}
                            onClick={() => setSelectedType(null)}
                          >
                            {t('productGrid.allProducts')}
                          </li>
                          {productTypes.map((type, index) => (
                            <li
                              key={`type-${type.id}`}
                              className={`${index !== productTypes.length - 1 ? 'pb-2' : ''} cursor-pointer ${selectedType === type.slug ? 'font-medium text-14' : ''}`}
                              onClick={() => setSelectedType(selectedType === type.slug ? null : type.slug)}
                            >
                              {type.name}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mb-[32px]">
                        <h2 className="font-light text-10 mb-2">{t('productGrid.concern')}</h2>
                        <ul>
                          <li
                            className={`pb-2 cursor-pointer ${!selectedConcern ? 'font-medium text-14' : ''}`}
                            onClick={() => setSelectedConcern(null)}
                          >
                            {t('productGrid.allProducts')}
                          </li>

                          {concerns.map((concern, index) => (
                            <li
                              key={`concern-${concern.id}`}
                              className={`${index !== concerns.length - 1 ? 'pb-2' : ''} cursor-pointer ${selectedConcern === concern.slug ? 'font-medium text-14' : ''}`}
                              onClick={() => setSelectedConcern(selectedConcern === concern.slug ? null : concern.slug)}
                            >
                              {concern.name}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mb-2">
                        <h2 className="font-light text-10 mb-2">{t('productGrid.sortBy')}</h2>
                        <ul>
                          <li
                            className={`pb-2 cursor-pointer ${orderBy === 'date' && sortOrder === 'desc' ? 'font-medium text-14' : ''}`}
                            onClick={() => { setOrderBy('date'); setSortOrder('desc'); }}
                          >
                            {t('productGrid.latest')}
                          </li>
                          <li
                            className={` cursor-pointer ${orderBy === 'popularity' && sortOrder === 'desc' ? 'font-medium text-14' : ''}`}
                            onClick={() => { setOrderBy('popularity'); setSortOrder('desc'); }}
                          >
                            {t('productGrid.bestsellers')}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-2 bg-white">
                    <hr className="border-t border-black mb-2" />
                    <button
                      onClick={toggleFilters}
                      className="w-full bg-black text-white pt-[10px] pb-[9px] font-normal text-14 text-center"
                    >
                      {t('productGrid.apply')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Product Grid */}
            <div
              className={`grid gap-2 flex-1 items-start content-start ${gridColumns} 
    md:transition-opacity md:duration-150 md:ease-in-out ${window.innerWidth >= 768
                  ? (gridVisible && !isFilterLoading ? 'opacity-100' : 'opacity-0')
                  : 'opacity-100'
                }`}
            >
              {products.map((product, index) => {
                const productFormula = getProductFormula(product);
                const productIsBestseller = isBestsellerProduct(product);
                const saleBadgeText = getSaleBadgeText(product);
                const productIsOnSale = isProductOnSale(product);

                return (
                  <div key={product.id} className="border border-black relative bg-white flex flex-col h-full">
                    <a href={`/store/product/${product.slug}`} className="flex flex-col h-full">
                      {(productIsOnSale || productIsBestseller) && (
                        <div className="absolute top-17 right-17 z-10">
                          <div className="hidden md:flex gap-2 items-end">
                            {productIsOnSale && (
                              <div className="border border-black px-2 pt-[4px] pb-[3px] flex items-center bg-white w-fit">
                                <span className='text-14 font-normal'>{saleBadgeText}</span>
                              </div>
                            )}

                            {productIsBestseller && (
                              <div className="border border-black pl-[6px] pr-[7px] pt-[4px] pb-[3px] flex items-center bg-white">
                                <svg className="-translate-y-[1px]"
                                  width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6.99994 1.18182L8.41813 3.58182L11.1163 2.88364L10.4181 5.58182L12.8181 7.00001L10.4181 8.41819L11.1163 11.1164L8.41813 10.4182L6.99994 12.8182L5.58176 10.4182L2.88358 11.1164L3.58176 8.41819L1.18176 7.00001L3.58176 5.58182L2.88358 2.88364L5.58176 3.58182L6.99994 1.18182Z" fill="black" stroke="black" strokeMiterlimit="10" />
                                </svg>
                                <span className="text-14 font-normal ml-[3px]">{t('productGrid.bestseller')}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col gap-2 md:hidden items-end">
                            {productIsBestseller && (
                              <div className="border border-black pl-[6px] pr-[7px] pt-[4px] pb-[3px] flex items-center bg-white">
                                <svg className="-translate-y-[1px]"
                                  width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6.99994 1.18182L8.41813 3.58182L11.1163 2.88364L10.4181 5.58182L12.8181 7.00001L10.4181 8.41819L11.1163 11.1164L8.41813 10.4182L6.99994 12.8182L5.58176 10.4182L2.88358 11.1164L3.58176 8.41819L1.18176 7.00001L3.58176 5.58182L2.88358 2.88364L5.58176 3.58182L6.99994 1.18182Z" fill="black" stroke="black" strokeMiterlimit="10" />
                                </svg>
                                <span className="text-14 font-normal ml-[3px]">{t('productGrid.bestseller')}</span>
                              </div>
                            )}

                            {productIsOnSale && (
                              <div className="border border-black px-2 pt-[4px] pb-[3px] flex items-center bg-white w-fit">
                                <span className='text-14 font-normal'>{saleBadgeText}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="aspect-square bg-[#BDBDBD] relative">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={product.images[0].src}
                            alt={decodeText(product.name)}
                            className="w-full h-full object-cover absolute inset-0"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-[#F5F5F5] absolute inset-0">
                            <span className="text-black text-14 font-light">{t('productGrid.noImage')}</span>
                          </div>
                        )}
                      </div>

                      <div className="border-t border-black flex flex-1">
                        <div className="flex-1 px-2 py-[4px] flex flex-col justify-center">
                          <h3 className="text-14 font-light mb-[4px]">{decodeText(product.name)}</h3>
                          <p className="text-14 font-normal">{formatPrice(product.price)}</p>
                        </div>

                        {productFormula && getAcfField(product, "product_number") && (
                          <div className="flex-shrink-0 border-l border-black">
                            <div
                              className="flex items-center justify-center text-14 font-normal text-white w-12 h-12 relative"
                              style={{
                                backgroundColor: productFormula.color
                              }}
                            >
                              <div className="absolute bottom-0 left-0 right-0 h-px bg-black transform translate-y-px"></div>
                              <span className="whitespace-nowrap" style={{ color: productFormula.text_color }}>
                                {productFormula.slug}{getAcfField(product, "product_number")}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductGrid;